import { MutableRefObject, useEffect, useRef } from 'react';

import {
  initializeTrackingModule,
  isTestEnvironment,
  TrackingConfig,
  TrackingInstance,
  TrackingModule,
} from '@lightspeed-payments/payments-portal-sdk';

export type EventType =
  | { type: 'trackEvent'; payload: Parameters<TrackingInstance['trackEvent']> }
  | { type: 'identify'; payload: Parameters<TrackingInstance['identify']> };

export interface TrackingModuleInitResult {
  trackingModuleRef: MutableRefObject<TrackingModule | null>;
  eventQueue: MutableRefObject<EventType[]>;
  initFailedRef: MutableRefObject<boolean>;
}

// Multiple Provider mounts that share the same configs share one SDK init
// (e.g. BusinessSnapshot, SnapshotAside, CapitalBanner all pass the same
// `[{ name: 'product', environment: ... }]`). Without this, each mount fires
// its own initializeTrackingModule network call.
const moduleCache = new Map<string, Promise<TrackingModule>>();

const getOrInitModule = (configs: TrackingConfig[]): Promise<TrackingModule> => {
  const key = JSON.stringify(configs);
  let promise = moduleCache.get(key);
  if (!promise) {
    promise = initializeTrackingModule({ configs }).catch((err) => {
      // Evict on failure so the next mount can retry under different
      // network conditions instead of inheriting the cached rejection.
      moduleCache.delete(key);
      throw err;
    });
    moduleCache.set(key, promise);
  }
  return promise;
};

// eslint-disable-next-line no-underscore-dangle
export const __resetTrackingModuleCacheForTests = (): void => {
  if (process.env.NODE_ENV !== 'test') return;
  moduleCache.clear();
};

/**
 * Initializes the SDK tracking module on Provider mount, buffers events
 * fired during init in a queue, and drains the queue against the module
 * once init resolves. Reports init failures via `onInitError`.
 *
 * Intentionally minimal: no retry-with-backoff (init failure is
 * observability-only and matches today's behavior); no AbortController
 * (no long-lived async work to cancel without retry); no config-change
 * re-init detection (callsites pass static literals); no `instances`
 * or `isTrackingModuleLoaded` exposed (no consumer reads them).
 */
export function useTrackingModuleInit(
  configs: TrackingConfig[],
  onInitError?: (error: unknown) => void
): TrackingModuleInitResult {
  const trackingModuleRef = useRef<TrackingModule | null>(null);
  const eventQueue = useRef<EventType[]>([]);
  const initFailedRef = useRef(false);

  // Capture the latest onInitError without retriggering init when the
  // parent passes a new callback identity on re-render.
  const onInitErrorRef = useRef(onInitError);
  useEffect(() => {
    onInitErrorRef.current = onInitError;
  });

  useEffect(() => {
    if (isTestEnvironment()) {
      // The SDK never loads in jest; mark init as permanently failed so
      // events are dropped rather than queued indefinitely.
      initFailedRef.current = true;
      eventQueue.current = [];
      return undefined;
    }

    let isMounted = true;

    getOrInitModule(configs)
      .then((module) => {
        if (!isMounted) return;
        trackingModuleRef.current = module;
        eventQueue.current.forEach(({ type, payload }) => {
          if (type === 'trackEvent') module.trackEvent(...payload);
          else module.identify(...payload);
        });
        eventQueue.current = [];
      })
      .catch((err: unknown) => {
        if (!isMounted) return;
        initFailedRef.current = true;
        eventQueue.current = [];
        onInitErrorRef.current?.(err);
      });

    return () => {
      isMounted = false;
    };
    // configs is expected to be a stable reference at every callsite; we
    // do not re-init when it changes by identity (no consumer changes
    // configs at runtime in practice).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { trackingModuleRef, eventQueue, initFailedRef };
}
